import React, { useEffect, useState, useCallback } from "react";
import "./../../../../../assets/css/Dashboard/ServicesKanban.scss";
import useTitanApp from "../../../../../hooks/useTitanApp";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Skeleton } from "@mui/material";
import {
  getDueName,
  getTotalServicesDueCount,
} from "../../../../../data/getStartDateForStages";
import "react-datepicker/dist/react-datepicker.css";
import "react-toastify/dist/ReactToastify.css";
import ServiceItemList from "./ServiceItemList";
import InfiniteScroll from "react-infinite-scroll-component";
import debounce from "lodash.debounce";
import moment from "moment";

function ServicesKanbanAccordian({
  service,
  currentIndex,
  stage,
  smsQueue,
  reSmsQueue,
  interventionQueue,
  declineQueue,
  rescheduleQueue,
  pdsQueue,
  orbitQueue,
  setCurrentSmsQueue,
  setCurrentReSmsQueue,
  setCurrentInterventionQueue,
  setCurrentDeclineQueue,
  setCurrentRescheduleQueue,
  setCurrentPdsQueue,
  setCurrentOrbitQueue,
  checksCount,
  setChecksCount,
  servicesUpdatedData,
  setShowPatientContactModal,
  setEligibilityScroll,
  setContactServiceID,
}) {
  const { pharmacyServiceState, appState } = useTitanApp();
  const { darkMode } = appState || {};
  const { isPharmacyServiceLoading, stageSortOrders } = pharmacyServiceState;
  // Defaults to ascending (the existing behavior) until the user picks a
  // direction via the Sort by Date button for this stage.
  const isSortAscending = stageSortOrders?.[stage?.stageType] ?? true;
  const privateServices = service || {};
  const [visibleServices, setVisibleServices] = useState({});
  const [hasMore, setHasMore] = useState(true);

  // Stack a patient's services only when they were handed out on the same day
  // (UTC), matching the API: opening/saving a stacked NMS merges only the
  // patient's NMS from that same day. Services without a matched patient are
  // never stacked together.
  const groupedClinicalServices = (
    privateServices?.clinicalServices || []
  ).reduce((acc, clinicalService) => {
    const { patientId, clinicalServiceDate, _id } = clinicalService;
    const hasPatient =
      patientId && patientId !== "00000000-0000-0000-0000-000000000000";
    const groupKey = hasPatient ? patientId : `no-patient-${_id}`;
    const serviceDate = moment.utc(clinicalServiceDate);
    const dateKey = serviceDate.isValid()
      ? serviceDate.format("YYYY-MM-DD")
      : `no-date-${_id}`;
    acc[groupKey] = acc[groupKey] || {};
    acc[groupKey][dateKey] = acc[groupKey][dateKey] || [];
    acc[groupKey][dateKey].push(clinicalService);
    return acc;
  }, {});

  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [currentDateIndices, setCurrentDateIndices] = useState({});

  const fetchMoreData = useCallback(() => {
    const groupKeys = Object.keys(groupedClinicalServices || {});
    if (currentGroupIndex >= groupKeys.length) {
      setHasMore(false);
      return;
    }

    const updatedServices = { ...visibleServices };
    let newGroupIndex = currentGroupIndex;
    let loadCount = 0;

    while (newGroupIndex < groupKeys.length && loadCount < 20) {
      const groupKey = groupKeys[newGroupIndex];
      const dates = Object.keys(groupedClinicalServices[groupKey] || {});
      const currentDateIndex = currentDateIndices[groupKey] || 0;

      for (let i = currentDateIndex; i < dates.length; i++) {
        const date = dates[i];
        if (!updatedServices[groupKey]) updatedServices[groupKey] = {};
        if (!updatedServices[groupKey][date]) {
          updatedServices[groupKey][date] =
            groupedClinicalServices[groupKey][date];
          loadCount += groupedClinicalServices[groupKey][date].length;
          if (loadCount >= 20) {
            setCurrentDateIndices((prev) => ({ ...prev, [groupKey]: i + 1 }));
            break;
          }
        }
      }

      if (loadCount < 20) newGroupIndex++;
    }

    setVisibleServices(updatedServices);
    setCurrentGroupIndex(newGroupIndex);
    if (newGroupIndex >= groupKeys.length) setHasMore(false);
  }, [
    groupedClinicalServices,
    currentGroupIndex,
    currentDateIndices,
    visibleServices,
  ]);

  const debouncedFetchData = useCallback(debounce(fetchMoreData, 500), [
    fetchMoreData,
  ]);

  useEffect(() => {
    debouncedFetchData();
    return () => debouncedFetchData.cancel();
  }, [debouncedFetchData]);

  useEffect(() => {
    const clinicalServices = service?.clinicalServices;
    if (!clinicalServices || clinicalServices.length === 0) return;

    const freshById = new Map();
    clinicalServices.forEach((cs) => freshById.set(cs._id, cs));

    setVisibleServices((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      let changed = false;
      const updated = {};
      for (const [pid, dates] of Object.entries(prev)) {
        updated[pid] = {};
        for (const [dk, services] of Object.entries(dates)) {
          updated[pid][dk] = services.map((s) => {
            const fresh = freshById.get(s._id);
            if (fresh && fresh !== s) {
              changed = true;
              return fresh;
            }
            return s;
          });
        }
      }
      return changed ? updated : prev;
    });
  }, [service?.clinicalServices]);

  // ---------- helper to compute a real, comparable timestamp for sorting ----------
  // Mirrors the date resolution the Redux-level sort already uses (this stage's
  // own startDate, falling back to the clinical service's date). Sorting must use
  // the actual date rather than the "days left" label above: that label collapses
  // every overdue item down to the single string "Overdue" regardless of how
  // overdue it is, so all overdue items tied at the same value and never sorted
  // relative to each other or interleaved with non-overdue items by real date -
  // stacked (often longer-in-pipeline, more likely overdue) patients ended up
  // clumped together separately from single cards instead of mixed in by date.
  const getComparableTime = (cs) => {
    const matchedStage = cs?.stages?.find(
      (s) => s?.stageType === stage?.stageType,
    );
    const dateVal = matchedStage?.startDate ?? cs?.clinicalServiceDate;
    const t = dateVal ? new Date(dateVal).getTime() : NaN;
    return Number.isFinite(t) ? t : Infinity;
  };

  return (
    <Accordion key={currentIndex} className="dueDate_accordion" defaultExpanded>
      <AccordionSummary
        key={currentIndex}
        expandIcon={
          <ExpandMoreIcon fontSize="14" className="expandIcon_dueDate" />
        }
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div>
          <p className="service_title service_duedate_title">
            {isPharmacyServiceLoading ? (
              <Skeleton
                sx={{ bgcolor: darkMode ? "grey.900" : "" }}
                height={30}
                animation="wave"
              />
            ) : (
              `${getDueName(service?.dueType)} ${getTotalServicesDueCount(
                service,
                stage?.stageName,
                service?.dueType,
              )}`
            )}
          </p>
        </div>
      </AccordionSummary>
      <AccordionDetails key={currentIndex} className="data-box-container">
        <InfiniteScroll
          className={`infinite-scroll-${stage?.stageName}`}
          dataLength={Object.keys(visibleServices).length}
          next={fetchMoreData}
          hasMore={hasMore}
          scrollThreshold={0.8}
          style={{ overflow: "auto" }}
        >
          {(() => {
            const allGroups = Object.values(
              visibleServices || {},
            ).flatMap((patientGroups) => Object.values(patientGroups || {}));
            if (!allGroups || allGroups.length === 0) return null;
            // Stacks are ordered against each other by their front card's
            // date only; items inside a stack keep their default order so the
            // sort direction never swaps which clinical service is in front.
            const groupsWithDays = allGroups.map((group) => ({
              group: group || [],
              parsed: getComparableTime(group?.[0]),
            }));

            groupsWithDays.sort((a, b) => {
              const va = a.parsed;
              const vb = b.parsed;

              return isSortAscending ? va - vb : vb - va;
            });

            return groupsWithDays.map(({ group: sortedGroupItems }, groupIndex) => {
              if ((sortedGroupItems?.length || 0) > 1) {
                const representativeItem = sortedGroupItems[0];
                return (
                  <div
                    className="stacked-cards-container"
                    key={`group-${groupIndex}-${representativeItem?._id ||
                      groupIndex}`}
                  >
                    {isPharmacyServiceLoading ? (
                      <Skeleton
                        key={`skeleton-${groupIndex}-0`}
                        sx={{ bgcolor: darkMode ? "grey.900" : "" }}
                        height={200}
                        animation="wave"
                      />
                    ) : (
                      <div
                        className="check_service"
                        key={`svc-${groupIndex}-0-${representativeItem?._id}`}
                      >
                        <ServiceItemList
                          service={representativeItem}
                          stage={stage}
                          index={0}
                          group={sortedGroupItems}
                          smsQueue={smsQueue}
                          reSmsQueue={reSmsQueue}
                          interventionQueue={interventionQueue}
                          declineQueue={declineQueue}
                          rescheduleQueue={rescheduleQueue}
                          pdsQueue={pdsQueue}
                          orbitQueue={orbitQueue}
                          servicesUpdatedData={servicesUpdatedData}
                          setCurrentSmsQueue={setCurrentSmsQueue}
                          setCurrentReSmsQueue={setCurrentReSmsQueue}
                          setCurrentInterventionQueue={
                            setCurrentInterventionQueue
                          }
                          setCurrentDeclineQueue={setCurrentDeclineQueue}
                          setCurrentRescheduleQueue={setCurrentRescheduleQueue}
                          setCurrentPdsQueue={setCurrentPdsQueue}
                          setCurrentOrbitQueue={setCurrentOrbitQueue}
                          checksCount={checksCount}
                          setChecksCount={setChecksCount}
                          setShowPatientContactModal={
                            setShowPatientContactModal
                          }
                          setEligibilityScroll={setEligibilityScroll}
                          setContactServiceID={setContactServiceID}
                        />
                      </div>
                    )}
                    <div className="bottom-stacked"></div>
                    <div className="bottom-stacked"></div>
                  </div>
                );
              }
              const serviceItem = sortedGroupItems[0];
              return isPharmacyServiceLoading ? (
                <Skeleton
                  key={`skeleton-single-${groupIndex}`}
                  sx={{ bgcolor: darkMode ? "grey.900" : "" }}
                  height={180}
                  animation="wave"
                />
              ) : (
                <div
                  className="check_service"
                  key={`single-${groupIndex}-${serviceItem?._id || groupIndex}`}
                >
                  <ServiceItemList
                    service={serviceItem}
                    stage={stage}
                    index={0}
                    group={sortedGroupItems}
                    smsQueue={smsQueue}
                    reSmsQueue={reSmsQueue}
                    interventionQueue={interventionQueue}
                    declineQueue={declineQueue}
                    rescheduleQueue={rescheduleQueue}
                    pdsQueue={pdsQueue}
                    orbitQueue={orbitQueue}
                    servicesUpdatedData={servicesUpdatedData}
                    setCurrentSmsQueue={setCurrentSmsQueue}
                    setCurrentReSmsQueue={setCurrentReSmsQueue}
                    setCurrentInterventionQueue={setCurrentInterventionQueue}
                    setCurrentDeclineQueue={setCurrentDeclineQueue}
                    setCurrentRescheduleQueue={setCurrentRescheduleQueue}
                    setCurrentPdsQueue={setCurrentPdsQueue}
                    setCurrentOrbitQueue={setCurrentOrbitQueue}
                    checksCount={checksCount}
                    setChecksCount={setChecksCount}
                    setShowPatientContactModal={setShowPatientContactModal}
                    setEligibilityScroll={setEligibilityScroll}
                    setContactServiceID={setContactServiceID}
                  />
                </div>
              );
            });
          })()}
        </InfiniteScroll>
      </AccordionDetails>
    </Accordion>
  );
}

export default React.memo(ServicesKanbanAccordian);
